import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  styled,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BusinessIcon from "@mui/icons-material/Business";
import ComputerIcon from "@mui/icons-material/Computer";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import SystemUpdateIcon from "@mui/icons-material/SystemUpdate";
import SearchIcon from "@mui/icons-material/Search";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import DeveloperModeIcon from "@mui/icons-material/DeveloperMode";
import BugReportIcon from "@mui/icons-material/BugReport";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import NightlightIcon from "@mui/icons-material/Nightlight";

import PageTitle from "../../components/PageTitle";
import Navbar from "../../components/Navbar";
import Footer from "../../components/footer";
import LetsTalk from "../../components/letsTalk";

const brandedPlaceholder = (label: string): string => {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 600 400'><defs><linearGradient id='g' x1='0' x2='1' y1='0' y2='1'><stop offset='0' stop-color='#2f81ad'/><stop offset='1' stop-color='#1d648c'/></linearGradient></defs><rect width='600' height='400' fill='url(%23g)'/><text x='50%' y='50%' fill='white' font-size='26' font-family='-apple-system, system-ui, sans-serif' text-anchor='middle' dominant-baseline='middle' font-weight='700'>${label}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

const HeroBg = styled(Box)(({ theme }) => ({
  backgroundColor: "#0d1620",
  color: "#fff",
  paddingTop: theme.spacing(14),
  paddingBottom: theme.spacing(11),
  [theme.breakpoints.down("md")]: {
    paddingTop: theme.spacing(11),
    paddingBottom: theme.spacing(8),
  },
}));

const HeroLabel = styled(Typography)({
  fontSize: "0.78rem",
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "#7ec1e2",
  marginBottom: 22,
});

const HeroTitle = styled(Typography)(({ theme }) => ({
  fontSize: "3.2rem",
  fontWeight: 700,
  lineHeight: 1.12,
  letterSpacing: "-0.02em",
  color: "#fff",
  marginBottom: theme.spacing(3),
  maxWidth: 800,
  [theme.breakpoints.down("md")]: { fontSize: "2.4rem" },
  [theme.breakpoints.down("sm")]: { fontSize: "1.9rem" },
}));

const HeroLead = styled(Typography)(({ theme }) => ({
  fontSize: "1.08rem",
  lineHeight: 1.65,
  color: "rgba(255, 255, 255, 0.72)",
  marginBottom: theme.spacing(4),
  maxWidth: 640,
}));

const PrimaryButton = styled(Button)({
  padding: "12px 24px",
  borderRadius: 6,
  fontWeight: 600,
  fontSize: "0.95rem",
  textTransform: "none",
  backgroundColor: "#2f81ad",
  color: "#fff",
  boxShadow: "none",
  "&:hover": { backgroundColor: "#1d648c", boxShadow: "none" },
  transition: "background-color 0.2s ease",
});

const GhostButton = styled(Button)(({ theme }) => ({
  padding: "11px 22px",
  borderRadius: 6,
  fontWeight: 600,
  fontSize: "0.95rem",
  textTransform: "none",
  color: "#fff",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  backgroundColor: "transparent",
  marginLeft: theme.spacing(1.5),
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.06)",
    borderColor: "rgba(255, 255, 255, 0.35)",
  },
  transition: "all 0.2s ease",
  [theme.breakpoints.down("sm")]: {
    marginLeft: 0,
    marginTop: theme.spacing(1.5),
  },
}));

const HeroChips = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  paddingTop: theme.spacing(4),
  borderTop: "1px solid rgba(255, 255, 255, 0.08)",
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
}));

const HeroChip = styled("span")({
  fontSize: "0.78rem",
  fontWeight: 600,
  color: "rgba(255, 255, 255, 0.85)",
  padding: "5px 11px",
  borderRadius: 999,
  backgroundColor: "rgba(255, 255, 255, 0.06)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
});

const Label = styled(Typography)({
  fontSize: "0.78rem",
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "#2f81ad",
  marginBottom: 14,
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "2.4rem",
  fontWeight: 700,
  lineHeight: 1.2,
  letterSpacing: "-0.02em",
  color: "#0f172a",
  marginBottom: theme.spacing(2),
  maxWidth: 760,
  [theme.breakpoints.down("md")]: { fontSize: "1.95rem" },
  [theme.breakpoints.down("sm")]: { fontSize: "1.65rem" },
}));

const Lead = styled(Typography)({
  fontSize: "1.02rem",
  lineHeight: 1.65,
  color: "rgba(15, 23, 42, 0.65)",
  maxWidth: 720,
});

const Card = styled(Box)(({ theme }) => ({
  height: "100%",
  padding: theme.spacing(3.5, 3.25),
  borderRadius: 10,
  backgroundColor: "#fff",
  border: "1px solid rgba(15, 23, 42, 0.1)",
  display: "flex",
  flexDirection: "column",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    borderColor: "rgba(47, 129, 173, 0.5)",
    boxShadow: "0 8px 24px -12px rgba(15, 23, 42, 0.15)",
  },
}));

const IconBadge = styled(Box)({
  width: 44,
  height: 44,
  borderRadius: 8,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(47, 129, 173, 0.08)",
  color: "#2f81ad",
  marginBottom: 20,
  "& svg": { fontSize: 22 },
});

const CardTitle = styled(Typography)({
  fontSize: "1.1rem",
  fontWeight: 700,
  color: "#0f172a",
  marginBottom: 8,
  letterSpacing: "-0.01em",
});

const CardCopy = styled(Typography)({
  fontSize: "0.92rem",
  lineHeight: 1.6,
  color: "rgba(15, 23, 42, 0.65)",
  marginBottom: 16,
  flex: 1,
});

const StepIndex = styled(Typography)({
  fontSize: "0.78rem",
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "rgba(15, 23, 42, 0.4)",
  marginBottom: 12,
});

const TechPill = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "9px 16px",
  borderRadius: 999,
  backgroundColor: "#fff",
  border: "1px solid rgba(15, 23, 42, 0.1)",
  color: "#0f172a",
  fontWeight: 600,
  fontSize: "0.88rem",
});

const TagRow = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
});

const Tag = styled("span")({
  fontSize: "0.72rem",
  fontWeight: 600,
  color: "#1d648c",
  padding: "3px 9px",
  borderRadius: 6,
  backgroundColor: "rgba(47, 129, 173, 0.08)",
  border: "1px solid rgba(47, 129, 173, 0.18)",
});

const ProjectCard = styled(Box)({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  backgroundColor: "#fff",
  borderRadius: 10,
  overflow: "hidden",
  border: "1px solid rgba(15, 23, 42, 0.1)",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    borderColor: "rgba(47, 129, 173, 0.5)",
    boxShadow: "0 12px 28px -16px rgba(15, 23, 42, 0.2)",
    "& img": { transform: "scale(1.04)" },
  },
});

const Thumb = styled("div")({
  position: "relative",
  width: "100%",
  aspectRatio: "16 / 10",
  overflow: "hidden",
  backgroundColor: "#f1f5f9",
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    transition: "transform 0.4s ease",
  },
});

const HERO_CHIPS = ["ERP", "CRM", "SCM", "Microservices", "Data analytics", "Modernization"];

const FEATURES = [
  {
    icon: <BusinessIcon />,
    title: "Enterprise business applications",
    description:
      "Comprehensive ERP, CRM, and SCM solutions that integrate critical business processes and data across teams.",
  },
  {
    icon: <ComputerIcon />,
    title: "Web applications",
    description:
      "Scalable, responsive web apps with robust backends and intuitive interfaces for internal tools or customer-facing products.",
  },
  {
    icon: <AccountTreeIcon />,
    title: "System integration",
    description:
      "Seamless integration between existing systems, databases, and third-party APIs to unify business processes.",
  },
  {
    icon: <ViewModuleIcon />,
    title: "Microservices architecture",
    description:
      "Modular, maintainable systems built on microservices for flexibility, independent scaling, and safer deployments.",
  },
  {
    icon: <ShowChartIcon />,
    title: "Data analytics platforms",
    description:
      "Custom analytics and dashboards that turn operational data into decisions — instrumented, queried, and visualized.",
  },
  {
    icon: <SystemUpdateIcon />,
    title: "Legacy modernization",
    description:
      "Transform outdated systems into modern, secure, and efficient applications while preserving critical business logic.",
  },
];

const PROCESS = [
  {
    n: "01",
    icon: <SearchIcon />,
    title: "Discovery & requirements",
    description:
      "Deep dive into your business goals, user needs, and technical constraints. Output: a written scope and milestone plan.",
  },
  {
    n: "02",
    icon: <ArchitectureIcon />,
    title: "Architecture & design",
    description:
      "Technical architecture and database schema designed up front. UX wireframes and a clickable prototype before production code.",
  },
  {
    n: "03",
    icon: <DeveloperModeIcon />,
    title: "Development",
    description:
      "Agile sprints, working software every Friday. Clean, maintainable code in your repository from day one with CI included.",
  },
  {
    n: "04",
    icon: <BugReportIcon />,
    title: "Testing & QA",
    description:
      "Unit, integration, and user acceptance testing. Stable, secure, and performance-tested before anything ships to production.",
  },
  {
    n: "05",
    icon: <CloudDoneIcon />,
    title: "Deployment",
    description:
      "Production rollout with monitoring, alerts, and observability. Cloud-native or on-prem — whichever your infrastructure requires.",
  },
  {
    n: "06",
    icon: <NightlightIcon />,
    title: "Support & enhancement",
    description:
      "SLA-backed support, monitoring, and ongoing feature work. We maintain what we build long after launch.",
  },
];

const TECHNOLOGIES = [
  "Java",
  "Spring Boot",
  "Node.js",
  "Express",
  "Python",
  "Django",
  "React",
  "Vue.js",
  "PostgreSQL",
  "MongoDB",
  "AWS",
  "Docker",
];

const PROJECTS = [
  {
    title: "Enterprise Resource Planning",
    description:
      "Comprehensive ERP with modules for inventory, finance, HR, and customer relationship management — replacing five legacy tools.",
    image: brandedPlaceholder("ERP System"),
    tags: ["Java", "Spring Boot", "React", "PostgreSQL"],
  },
  {
    title: "Supply chain platform",
    description:
      "End-to-end supply chain solution with real-time tracking, inventory forecasting, and a vendor management portal.",
    image: brandedPlaceholder("SCM Platform"),
    tags: ["Node.js", "Express", "Vue.js", "MongoDB"],
  },
  {
    title: "Financial analytics dashboard",
    description:
      "Interactive financial reporting system with data visualization, predictive analytics, and automated reporting workflows.",
    image: brandedPlaceholder("Analytics Dashboard"),
    tags: ["Python", "Django", "D3.js", "AWS"],
  },
];

const CustomSoftwarePage: React.FC = () => {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const [featuresVisible, setFeaturesVisible] = useState(false);
  const [processVisible, setProcessVisible] = useState(false);
  const featuresRef = useRef<HTMLDivElement>(null);
  const processRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
    const watchers: Array<() => void> = [];
    [
      [featuresRef, setFeaturesVisible] as const,
      [processRef, setProcessVisible] as const,
    ].forEach(([ref, setter]) => {
      if (!ref.current) return;
      const obs = new IntersectionObserver(
        ([e]) => e.isIntersecting && setter(true),
        { threshold: 0.15 }
      );
      obs.observe(ref.current);
      watchers.push(() => obs.disconnect());
    });
    return () => watchers.forEach((w) => w());
  }, []);

  return (
    <Box sx={{ bgcolor: "#fff" }}>
      <PageTitle title="Custom Software Development" />
      <Navbar />

      <HeroBg component="section" aria-labelledby="lms-heading">
        <Container maxWidth="lg">
          <Box
            sx={{
              opacity: mounted ? 1 : 0,
              transform: mounted ? "translateY(0)" : "translateY(12px)",
              transition: "opacity 0.5s ease, transform 0.5s ease",
            }}
          >
            <HeroLabel>Custom software development</HeroLabel>
            <HeroTitle id="lms-heading" variant="h1">
              Software built around your business.
            </HeroTitle>
            <HeroLead>
              ERP, CRM, internal tools, microservices, analytics — designed
              and built by senior engineers for the way your team actually
              works. From discovery to deployment.
            </HeroLead>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
              }}
            >
              <PrimaryButton
                endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
                onClick={() => navigate("/contacts")}
                disableElevation
              >
                Start your project
              </PrimaryButton>
              <GhostButton onClick={() => navigate("/portfolio")}>
                See our work
              </GhostButton>
            </Box>
            <HeroChips>
              {HERO_CHIPS.map((chip) => (
                <HeroChip key={chip}>{chip}</HeroChip>
              ))}
            </HeroChips>
          </Box>
        </Container>
      </HeroBg>

      <Box
        ref={featuresRef}
        component="section"
        sx={{ py: { xs: 8, md: 11 }, backgroundColor: "#fff" }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              mb: { xs: 5, md: 7 },
              opacity: featuresVisible ? 1 : 0,
              transform: featuresVisible
                ? "translateY(0)"
                : "translateY(12px)",
              transition: "opacity 0.5s ease, transform 0.5s ease",
            }}
          >
            <Label>What we build</Label>
            <SectionTitle variant="h2">
              Custom software, end to end
            </SectionTitle>
            <Lead>
              Six product areas where most of our custom-software engagements
              concentrate. Mix and match — every build is scoped to what you
              actually need.
            </Lead>
          </Box>

          <Grid container spacing={2.5}>
            {FEATURES.map((f, i) => (
              <Grid item xs={12} sm={6} md={4} key={f.title}>
                <Box
                  sx={{
                    height: "100%",
                    opacity: featuresVisible ? 1 : 0,
                    transform: featuresVisible
                      ? "translateY(0)"
                      : "translateY(12px)",
                    transition: `opacity 0.45s ease ${
                      0.05 + i * 0.05
                    }s, transform 0.45s ease ${0.05 + i * 0.05}s`,
                  }}
                >
                  <Card>
                    <IconBadge>{f.icon}</IconBadge>
                    <CardTitle>{f.title}</CardTitle>
                    <CardCopy>{f.description}</CardCopy>
                  </Card>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box
        ref={processRef}
        component="section"
        sx={{ py: { xs: 8, md: 11 }, backgroundColor: "#f8fafc" }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              mb: { xs: 5, md: 7 },
              opacity: processVisible ? 1 : 0,
              transform: processVisible
                ? "translateY(0)"
                : "translateY(12px)",
              transition: "opacity 0.5s ease, transform 0.5s ease",
            }}
          >
            <Label>How we build it</Label>
            <SectionTitle variant="h2">
              Six stages, predictable delivery
            </SectionTitle>
            <Lead>
              Every custom-software engagement runs the same process. You
              always know what's happening this week, what's next, and when
              it ships.
            </Lead>
          </Box>

          <Grid container spacing={2.5}>
            {PROCESS.map((step, i) => (
              <Grid item xs={12} sm={6} md={4} key={step.n}>
                <Box
                  sx={{
                    height: "100%",
                    opacity: processVisible ? 1 : 0,
                    transform: processVisible
                      ? "translateY(0)"
                      : "translateY(12px)",
                    transition: `opacity 0.45s ease ${
                      0.05 + i * 0.05
                    }s, transform 0.45s ease ${0.05 + i * 0.05}s`,
                  }}
                >
                  <Card>
                    <IconBadge>{step.icon}</IconBadge>
                    <StepIndex>Step {step.n}</StepIndex>
                    <CardTitle>{step.title}</CardTitle>
                    <CardCopy>{step.description}</CardCopy>
                  </Card>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box
        component="section"
        sx={{ py: { xs: 8, md: 11 }, backgroundColor: "#fff" }}
      >
        <Container maxWidth="lg">
          <Box sx={{ mb: { xs: 4, md: 5 } }}>
            <Label>Stack</Label>
            <SectionTitle variant="h2">What we work with</SectionTitle>
            <Lead>
              No religious wars over stacks. We pick the technology that fits
              your team's skills, your performance budget, and your operational
              constraints.
            </Lead>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
            {TECHNOLOGIES.map((t) => (
              <TechPill key={t}>
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 16, color: "#2f81ad" }}
                />
                {t}
              </TechPill>
            ))}
          </Box>
        </Container>
      </Box>

      <Box
        component="section"
        sx={{ py: { xs: 8, md: 11 }, backgroundColor: "#f8fafc" }}
      >
        <Container maxWidth="lg">
          <Box sx={{ mb: { xs: 5, md: 7 } }}>
            <Label>Case studies</Label>
            <SectionTitle variant="h2">
              Custom software we've shipped
            </SectionTitle>
          </Box>

          <Grid container spacing={2.5}>
            {PROJECTS.map((p) => (
              <Grid item xs={12} sm={6} md={4} key={p.title}>
                <ProjectCard>
                  <Thumb>
                    <img src={p.image} alt={p.title} loading="lazy" />
                  </Thumb>
                  <Box
                    sx={{
                      p: 3,
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <CardTitle>{p.title}</CardTitle>
                    <CardCopy sx={{ flex: 1, mb: 2 }}>{p.description}</CardCopy>
                    <TagRow>
                      {p.tags.map((t) => (
                        <Tag key={t}>{t}</Tag>
                      ))}
                    </TagRow>
                  </Box>
                </ProjectCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <LetsTalk />
      <Footer />
    </Box>
  );
};

export default CustomSoftwarePage;
